import styled from "@emotion/styled";
import { Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import { useJournalEntryList } from "features/finances";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EthiopianDateField } from "../../components/EthiopianDateField";
import { JournalEntryModal } from "../Modals/JournalEntryModal";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

export const JournalEntriesTable = () => {
  const { t } = useTranslation();
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault("")
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault("")
  );
  const [filterDates, setFilterDates] = useState({ startDate, endDate });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);

  const handleFilter = () => {
    setFilterDates({ startDate, endDate });
  };

  const { data: journalEntries = [], isLoading } = useJournalEntryList({
    churchId: selectedChurch?.id,
    startDate: filterDates.startDate,
    endDate: filterDates.endDate,
    page,
    pageSize
  });

  const journalEntriesData = journalEntries?.data || [];

  const columns = [
    {
      field: "transaction_number",
      headerName: "Journal ID",
      flex: 1
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      valueFormatter: ({ value }) => moment(value).format("MM/DD/YYYY")
    },
    {
      field: "account_name",
      headerName: "Account",
      flex: 2,
      valueGetter: ({ row }) => row.chart_of_account?.account_name || ""
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2
    },
    {
      field: "debit",
      headerName: "Debit",
      flex: 1,
      valueGetter: ({ row }) =>
        row.entry_type === "debit" ? Number(row.amount).toLocaleString() : ""
    },
    {
      field: "credit",
      headerName: "Credit",
      flex: 1,
      valueGetter: ({ row }) =>
        row.entry_type === "credit" ? Number(row.amount).toLocaleString() : ""
    }
  ];

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <EthiopianDateField
            size="small"
            label="Start date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <EthiopianDateField
            size="small"
            label="End date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
          <Button onClick={handleFilter}>Filter</Button>
        </Stack>
        <Button onClick={() => setOpen(true)}>Add Journal Entry</Button>
      </Stack>

      <DataGrid
        autoHeight
        rows={journalEntriesData}
        loading={isLoading}
        columns={columns}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows")
        }}
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        sx={{
          m: 0,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#E6F7F8",
            borderRadius: "10px 10px 0px 0px",
            color: "#000000",
            border: "none"
          }
        }}
      />
      <JournalEntryModal open={open} onClose={() => setOpen(false)} />
    </Container>
  );
};
